import {
  ArrowRightIcon,
  BoltIcon,
  ChartBarSquareIcon,
  ChatBubbleLeftRightIcon,
  CheckBadgeIcon,
  CircleStackIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import React from 'react';
import { Helmet } from 'react-helmet-async';

const DEALER_LOGIN_URL = 'https://staging.carlotpilot.com/login';
const REQUEST_DEMO_URL = 'mailto:admin@carlotpilot.com?subject=CarLotPilot%20Demo%20Request';

const operatingStats = [
  {
    value: '1 desk',
    label: 'Inventory, CRM, deals, servicing, and follow-through in one operating system.',
  },
  {
    value: 'BHPH-first',
    label:
      'Promise tracking, late-fee workflows, payments, and collection follow-up built into the core.',
  },
  {
    value: 'Faster launch',
    label: 'Imports, public sites, and dealer-facing automation without stitching tools together.',
  },
];

const productPillars = [
  {
    title: 'Deal Desk That Closes Faster',
    description:
      'Quote, structure, and move deals without bouncing between spreadsheets, CRM tabs, and separate finance tools.',
    icon: CircleStackIcon,
  },
  {
    title: 'Collections That Stay Visible',
    description:
      'Worklists, promise tracking, payment routing, and reminder operations stay attached to the account instead of disappearing into notes.',
    icon: ChatBubbleLeftRightIcon,
  },
  {
    title: 'Imports, Websites, and Marketing',
    description:
      'Bring inventory in fast, syndicate when your plan allows it, and keep your public site aligned with live inventory.',
    icon: ChartBarSquareIcon,
  },
  {
    title: 'Operational Guardrails',
    description:
      'Tenant-aware permissions, auditability, and entitlement controls keep dealership ops clean as your rooftop grows.',
    icon: ShieldCheckIcon,
  },
];

const rolloutSteps = [
  'Stand up inventory, customer records, and website presence without old-system drag.',
  'Run sales, BHPH servicing, documents, and collections from the same dealer workspace.',
  'Add automation and AI where it earns time back instead of creating another dashboard to babysit.',
];

const CarLotPilotLandingPage: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>CarLotPilot | Modern BHPH Dealership Management</title>
        <meta
          name="description"
          content="CarLotPilot gives BHPH and independent dealers a cleaner operating system for inventory, CRM, deals, payments, collections, and customer follow-through."
        />
      </Helmet>

      <div className="min-h-screen overflow-hidden bg-[#04111f] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(22,187,198,0.22),_transparent_30%),radial-gradient(circle_at_75%_15%,_rgba(255,122,74,0.28),_transparent_25%),linear-gradient(180deg,_rgba(4,17,31,0.88),_rgba(4,17,31,1))]" />
        <div className="absolute inset-y-0 right-0 w-full bg-[linear-gradient(110deg,_transparent_25%,_rgba(4,17,31,0.24)_45%,_rgba(4,17,31,0.9)_75%)]" />

        <div className="relative mx-auto flex min-h-screen max-w-7xl flex-col px-6 pb-14 pt-6 lg:px-8">
          <header className="flex flex-col gap-4 rounded-[28px] border border-white/10 bg-white/5 px-5 py-4 backdrop-blur md:flex-row md:items-center md:justify-between md:px-6">
            <div className="flex items-center gap-4">
              <img
                src="/images/CarLotPilot_Logo.png"
                alt="CarLotPilot"
                className="h-12 w-auto sm:h-14"
              />
              <div>
                <p className="text-xs uppercase tracking-[0.28em] text-[#70f5f2]">
                  SaaS Operating System
                </p>
                <p className="text-sm text-slate-300">
                  Built for BHPH rooftops that need speed, visibility, and control.
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <a
                href={REQUEST_DEMO_URL}
                className="inline-flex items-center justify-center rounded-full border border-white/15 px-5 py-3 text-sm font-semibold text-white transition hover:border-[#70f5f2]/60 hover:bg-white/5"
              >
                Request a Demo
              </a>
              <a
                href={DEALER_LOGIN_URL}
                className="inline-flex items-center justify-center rounded-full bg-[#11b8c6] px-5 py-3 text-sm font-semibold text-[#04111f] transition hover:bg-[#5df0ea]"
              >
                Dealer Login
              </a>
            </div>
          </header>

          <main className="relative mt-8 grid flex-1 gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:gap-12">
            <section className="max-w-2xl py-4 lg:py-10">
              <p
                className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#70f5f2]/25 bg-[#08243b] px-4 py-2 text-xs uppercase tracking-[0.3em] text-[#9cf8f4]"
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                <BoltIcon className="h-4 w-4" />
                Own the rooftop. Move the metal. Protect the portfolio.
              </p>

              <h1
                className="max-w-3xl text-5xl font-semibold uppercase leading-[0.95] text-white sm:text-6xl lg:text-7xl"
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                CarLotPilot gives modern BHPH dealers a cleaner way to operate.
              </h1>

              <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-200 sm:text-xl">
                Replace DealerCenter-era clutter with a single workflow for inventory, CRM, deal
                structuring, payments, collections, public websites, and customer follow-through.
              </p>

              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href={REQUEST_DEMO_URL}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#ff7a4a] px-7 py-4 text-base font-semibold text-[#04111f] transition hover:bg-[#ff9a75]"
                >
                  Book a Product Walkthrough
                  <ArrowRightIcon className="h-5 w-5" />
                </a>
                <a
                  href={DEALER_LOGIN_URL}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-7 py-4 text-base font-semibold text-white transition hover:border-white/40 hover:bg-white/10"
                >
                  Access Your Dealer Workspace
                </a>
              </div>

              <div className="mt-10 grid gap-4 sm:grid-cols-3">
                {operatingStats.map((stat) => (
                  <div
                    key={stat.value}
                    className="rounded-[22px] border border-white/10 bg-white/6 p-5 shadow-[0_18px_45px_rgba(0,0,0,0.18)] backdrop-blur"
                  >
                    <p
                      className="text-xl uppercase tracking-[0.18em] text-[#70f5f2]"
                      style={{ fontFamily: "'Oswald', sans-serif" }}
                    >
                      {stat.value}
                    </p>
                    <p className="mt-3 text-sm leading-6 text-slate-300">{stat.label}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="relative flex items-center justify-center lg:justify-end">
              <div className="relative w-full max-w-[38rem]">
                <div className="absolute -left-4 top-5 h-24 w-24 rounded-full bg-[#11b8c6]/35 blur-3xl" />
                <div className="absolute -right-6 bottom-6 h-28 w-28 rounded-full bg-[#ff7a4a]/30 blur-3xl" />

                <div className="overflow-hidden rounded-[32px] border border-white/10 bg-[#0b1f35] shadow-[0_32px_90px_rgba(0,0,0,0.42)]">
                  <div className="relative h-[25rem] sm:h-[30rem]">
                    <img
                      src="/images/hero-bg.jpg"
                      alt="CarLotPilot product hero"
                      className="h-full w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-[linear-gradient(135deg,_rgba(4,17,31,0.2),_rgba(4,17,31,0.78)_65%,_rgba(4,17,31,0.98))]" />

                    <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6">
                      <div className="rounded-[26px] border border-white/10 bg-[#07192d]/85 p-5 backdrop-blur">
                        <div
                          className="flex items-center gap-3 text-sm uppercase tracking-[0.22em] text-[#9cf8f4]"
                          style={{ fontFamily: "'Oswald', sans-serif" }}
                        >
                          <CheckBadgeIcon className="h-5 w-5" />
                          What the desk needs, without the drag
                        </div>

                        <div className="mt-5 space-y-4">
                          {productPillars.slice(0, 3).map((pillar) => {
                            const Icon = pillar.icon;
                            return (
                              <div
                                key={pillar.title}
                                className="rounded-2xl border border-white/8 bg-white/5 p-4"
                              >
                                <div className="flex items-start gap-3">
                                  <div className="rounded-2xl bg-[#11b8c6]/12 p-2 text-[#70f5f2]">
                                    <Icon className="h-5 w-5" />
                                  </div>
                                  <div>
                                    <h2 className="text-base font-semibold text-white">
                                      {pillar.title}
                                    </h2>
                                    <p className="mt-1 text-sm leading-6 text-slate-300">
                                      {pillar.description}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </main>

          <section className="relative mt-6 grid gap-6 rounded-[32px] border border-white/10 bg-[#07182b]/80 px-6 py-8 shadow-[0_22px_70px_rgba(0,0,0,0.22)] backdrop-blur lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
            <div>
              <p
                className="text-sm uppercase tracking-[0.28em] text-[#70f5f2]"
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                Why dealers switch
              </p>
              <h2
                className="mt-3 text-3xl font-semibold uppercase text-white sm:text-4xl"
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                Stop running sales, servicing, and collections from disconnected tools.
              </h2>
              <p className="mt-4 max-w-xl text-base leading-7 text-slate-300">
                CarLotPilot is built for independent dealers who need the entire operation visible
                at once: where the lead sits, what the deal needs, which payment is due, and what
                the customer should see next.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {productPillars.map((pillar) => {
                const Icon = pillar.icon;
                return (
                  <div
                    key={pillar.title}
                    className="rounded-[26px] border border-white/10 bg-white/5 p-5"
                  >
                    <div className="flex items-center gap-3 text-[#70f5f2]">
                      <Icon className="h-6 w-6" />
                      <h3 className="text-lg font-semibold text-white">{pillar.title}</h3>
                    </div>
                    <p className="mt-3 text-sm leading-6 text-slate-300">{pillar.description}</p>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="relative mt-6 grid gap-6 rounded-[32px] border border-white/10 bg-gradient-to-r from-[#0a2540] via-[#0d3457] to-[#072238] px-6 py-8 lg:grid-cols-[0.95fr_1.05fr] lg:px-8">
            <div>
              <p
                className="text-sm uppercase tracking-[0.28em] text-[#ffd89b]"
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                Launch rhythm
              </p>
              <h2
                className="mt-3 text-3xl font-semibold uppercase text-white sm:text-4xl"
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                Go from inherited chaos to a tighter operating loop.
              </h2>
            </div>

            <div className="space-y-4">
              {rolloutSteps.map((step, index) => (
                <div
                  key={step}
                  className="flex gap-4 rounded-[24px] border border-white/10 bg-white/6 p-4"
                >
                  <div
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#ff7a4a] text-base font-semibold text-[#04111f]"
                    style={{ fontFamily: "'Oswald', sans-serif" }}
                  >
                    0{index + 1}
                  </div>
                  <p className="pt-1 text-sm leading-6 text-slate-100">{step}</p>
                </div>
              ))}
            </div>
          </section>

          <footer className="relative mt-6 flex flex-col gap-6 rounded-[28px] border border-white/10 bg-white/5 px-6 py-6 text-sm text-slate-300 backdrop-blur md:flex-row md:items-center md:justify-between">
            <div>
              <p className="font-medium text-white">
                CarLotPilot is built for BHPH and independent used-car operations.
              </p>
              <p className="mt-1">
                Inventory, CRM, deal structuring, payments, collections, and customer follow-through
                under one roof.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <a
                href={REQUEST_DEMO_URL}
                className="inline-flex items-center justify-center rounded-full border border-white/15 px-5 py-3 font-semibold text-white transition hover:border-[#70f5f2]/60 hover:bg-white/5"
              >
                Talk to CarLotPilot
              </a>
              <a
                href={DEALER_LOGIN_URL}
                className="inline-flex items-center justify-center rounded-full bg-[#11b8c6] px-5 py-3 font-semibold text-[#04111f] transition hover:bg-[#5df0ea]"
              >
                Dealer Login
              </a>
            </div>
          </footer>
        </div>
      </div>
    </>
  );
};

export default CarLotPilotLandingPage;
